import React from "react";
import {
  Button,
  IconText,
  Image,
  Input,
} from "@wac-ui-dashboard/wac_component_library";
import Select from "react-select";
import useAddSubscriptionPlan from "./useAddSubscriptionPlan";
import PricingPanel from "./PricingPanel";

/**
 * Marks a label whose field the schema requires. It only mirrors the rules held in
 * useAddSubscriptionPlan - a mark with no matching rule, or a rule with no mark, is the bug.
 */
const Required = () => <span className="pro-required-mark">*</span>;

const AddSubscriptionPlan = ({ isStickyFooter, handleCreateClick, refetch }) => {
  const {
    formik,
    isEdit,
    pricing,
    sizeList,
    storeList,
    statusObj,
    storesObj,
    imageName,
    audienceObj,
    mintedNames,
    planStatuses,
    planAudiences,
    menuStoreObj,
    isPlanSaving,
    imagePreview,
    lockedSizes,
    selectedRows,
    isPerSizePricing,
    perSizeField,
    aliasImageName,
    mainProductObj,
    mainProductList,
    isPricingLoading,
    isImageUploading,
    aliasImagePreview,
    isProductsFetching,
    isAliasImageUploading,
    activeSubscriptionCount,
    fieldProps,
    setFieldValueTouched,
    handleSizesChange,
    handleMenuStoreChange,
    handleAllStoresChange,
    handleMainProductChange,
    handleImageChange,
    getFieldError,
    closeModal,
  } = useAddSubscriptionPlan(handleCreateClick, refetch);

  // Several sizes are several plans, each named for the size it sells - so the name is the
  // catalogue's to give and there is no single box that could hold it.
  const namedByCatalogue = mintedNames.length > 1;

  return (
    <div className="row">
      <div className="input-wrap pro-mb-4">
        {isEdit && activeSubscriptionCount > 0 && (
          <div className="pro-p-3 pro-mb-4 pro-bg-light rounded-3">
            <p className="pro-font-sm pro-mb-0">
              {activeSubscriptionCount} active subscription
              {activeSubscriptionCount === 1 ? "" : "s"} were sold on this plan.
              They keep the terms they were bought on — editing a published plan
              supersedes it with a new version.
            </p>
          </div>
        )}

        <Input
          label={
            <>
              Plan Name <Required />
            </>
          }
          type="text"
          id="name"
          name="name"
          className={`pro-input lg ${getFieldError("name") && " error"}`}
          {...fieldProps("name")}
          error={getFieldError("name")}
          errorMessage={getFieldError("name")}
        />
        {namedByCatalogue && (
          <span className="pro-font-xs pro-mb-4 pro-d-block">
            Each size takes this name with its own size on the end —{" "}
            {mintedNames.join(", ")}.
          </span>
        )}

        <Input
          label={
            <>
              Plan Name (Arabic) <Required />
            </>
          }
          type="text"
          id="alias_name"
          name="alias_name"
          className={`pro-input lg ${getFieldError("alias_name") && " error"}`}
          {...fieldProps("alias_name")}
          error={getFieldError("alias_name")}
          errorMessage={getFieldError("alias_name")}
        />

        <div className="input-wrap pro-mb-4">
          <label htmlFor="description" className="pro-font-sm pro-mb-1 pro-fw-medium">
            Description <Required />
          </label>
          <textarea
            id="description"
            name="description"
            rows={3}
            className={`pro-input lg ${getFieldError("description") && " error"}`}
            {...fieldProps("description")}
          />
          {getFieldError("description") && (
            <span className="error-text">{getFieldError("description")}</span>
          )}
        </div>

        <div className="input-wrap pro-mb-4">
          <label
            htmlFor="alias_description"
            className="pro-font-sm pro-mb-1 pro-fw-medium"
          >
            Description (Arabic) <Required />
          </label>
          <textarea
            id="alias_description"
            name="alias_description"
            rows={3}
            className={`pro-input lg ${
              getFieldError("alias_description") && " error"
            }`}
            {...fieldProps("alias_description")}
          />
          {getFieldError("alias_description") && (
            <span className="error-text">
              {getFieldError("alias_description")}
            </span>
          )}
        </div>

        {/*
          The outlet whose menu the item is chosen from, which is not the same thing as the
          outlets the plan is sold at. Menus are held per outlet, and a plan names a single
          item - choose it from the wrong menu and there is no second line to absorb the
          mistake, so the plan cannot be redeemed where it was sold. It travels with the line
          as its provenance, so an edit can reopen on the menu the item came from.
        */}
        <div className="input-wrap pro-mb-4">
          <label htmlFor="menu_customer_key" className="pro-font-sm pro-mb-1 pro-fw-medium">
            Choose the item from this outlet's menu <Required />
          </label>
          <Select
            id="menu_customer_key"
            name="menu_customer_key"
            placeholder={"Select an outlet"}
            className={`pro-input lg ${
              getFieldError("menu_customer_key") && " error"
            }`}
            classNamePrefix="pro-input"
            options={storeList ?? []}
            getOptionLabel={(option) => option?.name}
            getOptionValue={(option) => option?._id}
            value={menuStoreObj ?? []}
            onBlur={formik.handleBlur("menu_customer_key")}
            onChange={(value) => handleMenuStoreChange(value)}
            menuPlacement="auto"
            isLoading={isProductsFetching}
          />
          {getFieldError("menu_customer_key") && (
            <span className="error-text">
              {getFieldError("menu_customer_key")}
            </span>
          )}
          <span className="pro-font-xs pro-mt-1 pro-d-block">
            Used only to look up beverages. Where the plan is sold is set below.
          </span>
        </div>

        {/*
          The drink, then its sizes. The catalogue answers the picker one row per size, so
          without this first step the only thing on offer was "Latte - Small" beside "Latte -
          Medium" with nothing saying they were the same drink.

          It filters and nothing more: leaving it empty lists every option below, which is how
          a drink that has no sizes of its own is still reachable.
        */}
        <div className="input-wrap pro-mb-4">
          <label
            htmlFor="main_product_id"
            className="pro-font-sm pro-mb-1 pro-fw-medium"
          >
            Beverage
          </label>
          <Select
            id="main_product_id"
            name="main_product_id"
            placeholder={
              formik.values.menu_customer_key
                ? "Select a beverage"
                : "Choose an outlet first"
            }
            className={"pro-input lg"}
            classNamePrefix="pro-input"
            options={mainProductList ?? []}
            getOptionLabel={(option) => option?.name}
            getOptionValue={(option) => option?.product_id}
            value={mainProductObj ?? []}
            isClearable
            onChange={(value) => handleMainProductChange(value)}
            menuPlacement="auto"
            isDisabled={!formik.values.menu_customer_key}
            isLoading={isProductsFetching}
          />
          <span className="pro-font-xs pro-mt-1 pro-d-block">
            {formik.values.main_product_id
              ? "Showing this beverage's sizes below."
              : "Leave blank to search every item instead."}
          </span>
        </div>

        <div className="input-wrap pro-mb-4">
          <label
            htmlFor="selected_options"
            className="pro-font-sm pro-mb-1 pro-fw-medium"
          >
            Sizes <Required />
          </label>
          <Select
            id="selected_options"
            name="selected_options"
            // The sizes are edited together, on a create and on an edit alike - opening one
            // size of a tile opens all of them.
            isMulti
            placeholder={
              formik.values.menu_customer_key
                ? "Select one or more sizes"
                : "Choose an outlet first"
            }
            className={`pro-input lg ${
              getFieldError("selected_options") && " error"
            }`}
            classNamePrefix="pro-input"
            options={sizeList ?? []}
            // The size alone once a beverage narrows the list, since the drink's name is
            // already sitting in the picker above; the full label when it does not.
            getOptionLabel={(option) =>
              formik.values.main_product_id && option?.variant_name
                ? `${option.variant_name} — KD ${Number(
                    option?.list_price ?? 0
                  ).toFixed(3)}`
                : `${option?.name} — KD ${Number(option?.list_price ?? 0).toFixed(
                    3
                  )}`
            }
            // Every size of a drink shares its parent's product_id, so keying on that would
            // collapse the nine rows of a Matcha Latte into one and highlight the wrong one.
            getOptionValue={(option) => option?.option_id}
            value={selectedRows ?? []}
            onBlur={formik.handleBlur("selected_options")}
            onChange={(value) => handleSizesChange(value)}
            menuPlacement="auto"
            isDisabled={!formik.values.menu_customer_key}
            isLoading={isProductsFetching}
          />
          {getFieldError("selected_options") && (
            <span className="error-text">
              {getFieldError("selected_options")}
            </span>
          )}
          {isEdit && !formik.values.menu_customer_key && (
            <span className="pro-font-xs pro-mt-1 pro-d-block">
              Showing the saved item. Pick an outlet above to change it.
            </span>
          )}
          {isEdit && lockedSizes.length > 0 && (
            <span className="pro-font-xs pro-mt-1 pro-d-block">
              Every size this beverage is sold in, edited together. Add one and it
              goes on sale; the ones already selling cannot be removed here — take a
              size off sale by unpublishing its own plan.
            </span>
          )}
          {!isEdit && selectedRows.length > 1 && (
            <span className="pro-font-xs pro-mt-1 pro-d-block">
              Saves {selectedRows.length} plans, one per size. They share the
              plan name and everything below, and are listed together under{" "}
              {mainProductObj?.[0]?.name ?? "the beverage"}. Each is bought and
              held on its own, so holding one size never blocks another.
            </span>
          )}
        </div>

        <Input
          label={
            <>
              Number of Items <Required />
            </>
          }
          type="number"
          id="quantity"
          name="quantity"
          className={`pro-input lg ${getFieldError("quantity") && " error"}`}
          {...fieldProps("quantity")}
          error={getFieldError("quantity")}
          errorMessage={getFieldError("quantity")}
        />

        {/*
          Send a discount or send a price - the service derives whichever is missing. Prices
          are never taken from the form: each line's list price is read from the catalog when
          the line resolves, so a doctored post cannot manufacture a discount.
        */}
        <div className="input-wrap pro-mb-4">
          <label className="pro-font-sm pro-mb-1 pro-fw-medium">Price by</label>
          <div className="pro-d-flex pro-gap-4">
            <div className="pro-check-box">
              <input
                type="radio"
                className="pro-check"
                id="pricing_mode_discount"
                name="pricing_mode"
                checked={formik.values.pricing_mode === "discount"}
                onChange={() => formik.setFieldValue("pricing_mode", "discount")}
              />
              <label htmlFor="pricing_mode_discount" className="pro-check-label">
                Discount %
              </label>
            </div>
            <div className="pro-check-box">
              <input
                type="radio"
                className="pro-check"
                id="pricing_mode_price"
                name="pricing_mode"
                checked={formik.values.pricing_mode === "price"}
                onChange={() => formik.setFieldValue("pricing_mode", "price")}
              />
              <label htmlFor="pricing_mode_price" className="pro-check-label">
                Selling price
              </label>
            </div>
          </div>
        </div>

        {/*
          Several sizes are several plans, and they do not sell alike - so each is priced on
          its own terms rather than sharing one percentage, which would price at least one of
          them wrong. Each box starts at whatever the single field held, so a group the team
          wants at one rate is still one number typed once.
        */}
        {isPerSizePricing ? (
          <div className="input-wrap pro-mb-4">
            <label className="pro-font-sm pro-mb-1 pro-fw-medium">
              {formik.values.pricing_mode === "discount"
                ? "Discount per size (%)"
                : "Selling price per size (KD)"}{" "}
              <Required />
            </label>
            <span className="pro-font-xs pro-mb-2 pro-d-block">
              {formik.values.pricing_mode === "discount"
                ? "Each size is its own plan, so each carries its own discount."
                : "Each size is its own plan. The discount is worked out per size, from that size's own list price."}
            </span>

            {(selectedRows ?? []).map((row) => (
              <Input
                key={row?.variant_id}
                label={row?.variant_name || row?.name}
                type="number"
                step={
                  formik.values.pricing_mode === "discount" ? undefined : "0.001"
                }
                id={`${perSizeField}.${row?.variant_id}`}
                name={`${perSizeField}.${row?.variant_id}`}
                className="pro-input lg"
                value={formik.values[perSizeField]?.[row?.variant_id] ?? ""}
                onChange={(event) =>
                  formik.setFieldValue(
                    `${perSizeField}.${row?.variant_id}`,
                    event.target.value
                  )
                }
                onBlur={formik.handleBlur}
              />
            ))}

            {/*
              One message for the set. The rule is "every size priced", which no single box
              is in a position to have broken.
            */}
            {getFieldError(perSizeField) && (
              <span className="error-text">{getFieldError(perSizeField)}</span>
            )}
          </div>
        ) : formik.values.pricing_mode === "discount" ? (
          <Input
            label={
              <>
                Discount (%) <Required />
              </>
            }
            type="number"
            id="discount_percent"
            name="discount_percent"
            className={`pro-input lg ${
              getFieldError("discount_percent") && " error"
            }`}
            {...fieldProps("discount_percent")}
            error={getFieldError("discount_percent")}
            errorMessage={getFieldError("discount_percent")}
          />
        ) : (
          <Input
            label={
              <>
                Selling Price (KD) <Required />
              </>
            }
            type="number"
            step="0.001"
            id="selling_price"
            name="selling_price"
            className={`pro-input lg ${
              getFieldError("selling_price") && " error"
            }`}
            {...fieldProps("selling_price")}
            error={getFieldError("selling_price")}
            errorMessage={getFieldError("selling_price")}
          />
        )}

        <PricingPanel
          pricing={pricing}
          isLoading={isPricingLoading}
          perPlan={namedByCatalogue}
        />

        {/*
          The badge the app prints over the plan, written rather than calculated. It sits
          here because it is usually a restatement of the discount above it - but it is free
          text ("Save 50%", "3 for the price of 2"), so it is typed rather than derived from
          the percentage. Both languages are required: the app falls back neither to the
          percentage nor from Arabic to English, so either box left empty ships a bare tile.
        */}
        <Input
          label={
            <>
              Discount Text <Required />
            </>
          }
          type="text"
          id="discount_text"
          name="discount_text"
          placeholder={"e.g. Save 50%"}
          className={`pro-input lg ${getFieldError("discount_text") && " error"}`}
          {...fieldProps("discount_text")}
          error={getFieldError("discount_text")}
          errorMessage={getFieldError("discount_text")}
        />

        <Input
          label={
            <>
              Discount Text (Arabic) <Required />
            </>
          }
          type="text"
          id="alias_discount_text"
          name="alias_discount_text"
          className={`pro-input lg ${
            getFieldError("alias_discount_text") && " error"
          }`}
          {...fieldProps("alias_discount_text")}
          error={getFieldError("alias_discount_text")}
          errorMessage={getFieldError("alias_discount_text")}
        />

        <Input
          label={
            <>
              Validity (days) <Required />
            </>
          }
          type="number"
          id="validity_days"
          name="validity_days"
          className={`pro-input lg ${getFieldError("validity_days") && " error"}`}
          {...fieldProps("validity_days")}
          error={getFieldError("validity_days")}
          errorMessage={getFieldError("validity_days")}
        />

        <Input
          label={
            <>
              Daily Limit <Required />
            </>
          }
          type="number"
          id="daily_limit"
          name="daily_limit"
          className={`pro-input lg ${getFieldError("daily_limit") && " error"}`}
          {...fieldProps("daily_limit")}
          error={getFieldError("daily_limit")}
          errorMessage={getFieldError("daily_limit")}
        />

        <Input
          label={"Purchase Limit (leave blank for unlimited)"}
          type="number"
          id="purchase_limit"
          name="purchase_limit"
          className={`pro-input lg ${
            getFieldError("purchase_limit") && " error"
          }`}
          {...fieldProps("purchase_limit")}
          error={getFieldError("purchase_limit")}
          errorMessage={getFieldError("purchase_limit")}
        />

        <div className="pro-check-box pro-py-2">
          <input
            type="checkbox"
            className="pro-check"
            id="all_stores"
            name="all_stores"
            checked={Boolean(formik.values.all_stores)}
            onChange={(e) => handleAllStoresChange(e?.target?.checked)}
          />
          <label htmlFor="all_stores" className="pro-check-label">
            Sell at all outlets
          </label>
        </div>

        {!formik.values.all_stores && (
          <div className="input-wrap pro-mb-4">
            <label htmlFor="stores" className="pro-font-sm pro-mb-1 pro-fw-medium">
              Outlets <Required />
            </label>
            <Select
              id="stores"
              name="stores"
              isMulti
              placeholder={"Select outlets"}
              className={`pro-input lg ${getFieldError("stores") && " error"}`}
              classNamePrefix="pro-input"
              options={storeList ?? []}
              getOptionLabel={(option) => option?.name}
              getOptionValue={(option) => option?._id}
              value={storesObj ?? []}
              onBlur={formik.handleBlur("stores")}
              onChange={(value) =>
                setFieldValueTouched(
                  "stores",
                  value?.map((item) => item?._id) ?? []
                )
              }
              menuPlacement="auto"
            />
            {getFieldError("stores") && (
              <span className="error-text">{getFieldError("stores")}</span>
            )}
          </div>
        )}

        {/*
          Grouping is a listing arrangement and nothing more - the plan is still sold, held
          and capped on its own, and holding one size never blocks buying another. The tile's
          name and artwork come from the beverage chosen above, so there is nothing further
          to fill in here.
        */}
        <div className="pro-check-box pro-py-2">
          <input
            type="checkbox"
            className="pro-check"
            id="group_by_product"
            name="group_by_product"
            // Sizes saved together are the case the tile exists for, so it is settled rather
            // than asked. Still a plain field on each plan afterwards, so any one of them can
            // be pulled out of the tile on its own edit form.
            checked={namedByCatalogue || Boolean(formik.values.group_by_product)}
            disabled={namedByCatalogue}
            onChange={(e) =>
              setFieldValueTouched("group_by_product", e?.target?.checked)
            }
          />
          <label htmlFor="group_by_product" className="pro-check-label">
            Show this plan under its beverage in the app
          </label>
        </div>
        <span className="pro-font-xs pro-mb-4 pro-d-block">
          {namedByCatalogue
            ? "Sizes saved together are always listed as one tile. You can pull any of them out later by editing that plan."
            : "Sizes of one drink collapse into a single tile the customer opens to choose between them. Needs at least two plans on the same beverage - on its own it goes on listing normally."}
        </span>

        {/*
          Which size the customer lands on. Only asked once there is more than one - a group of
          one opens on the only size it has - and left blank is a real answer, not an unfinished
          field: the app leads with the dearest until somebody says otherwise.
        */}
        {namedByCatalogue && (
          <div className="input-wrap pro-mb-4">
            <label
              htmlFor="default_variant"
              className="pro-font-sm pro-mb-1 pro-fw-medium"
            >
              Size the app opens on
            </label>
            <select
              id="default_variant"
              name="default_variant"
              className="pro-input lg"
              value={formik.values.default_variant ?? ""}
              onChange={(e) =>
                setFieldValueTouched("default_variant", e?.target?.value)
              }
            >
              <option value="">Dearest size</option>
              {(selectedRows ?? []).map((row) => (
                <option key={row?.option_id} value={row?.variant_id ?? ""}>
                  {row?.variant_name || row?.name}
                </option>
              ))}
            </select>
            <span className="pro-font-xs pro-mt-1 pro-d-block">
              The size already selected when the customer opens the tile, and the
              price shown on the card. Leave on "Dearest size" to let the app
              choose. A size that is sold out or already held is passed over.
            </span>
          </div>
        )}

        <div className="input-wrap pro-mb-4">
          <label htmlFor="terms" className="pro-font-sm pro-mb-1 pro-fw-medium">
            Terms <Required />
          </label>
          <textarea
            id="terms"
            name="terms"
            rows={3}
            className={`pro-input lg ${getFieldError("terms") && " error"}`}
            {...fieldProps("terms")}
          />
          {getFieldError("terms") && (
            <span className="error-text">{getFieldError("terms")}</span>
          )}
        </div>

        <div className="input-wrap pro-mb-4">
          <label htmlFor="alias_terms" className="pro-font-sm pro-mb-1 pro-fw-medium">
            Terms (Arabic) <Required />
          </label>
          <textarea
            id="alias_terms"
            name="alias_terms"
            rows={3}
            className={`pro-input lg ${getFieldError("alias_terms") && " error"}`}
            {...fieldProps("alias_terms")}
          />
          {getFieldError("alias_terms") && (
            <span className="error-text">{getFieldError("alias_terms")}</span>
          )}
        </div>

        {/*
          image and alias_image hold URLs, not files - the artwork is uploaded first and its
          URL sent on create. They are separate fields because Arabic artwork is usually a
          different file rather than the same one flipped, which is also why only the English
          one is required: artwork carrying no text serves both.
        */}
        <div className="input-wrap pro-mb-4">
          <label htmlFor="image" className={`pro-font-sm pro-mb-1 pro-fw-medium`}>
            Plan Image <Required />
          </label>
          <div className="input-drag">
            <input
              type="file"
              accept=".jpeg,.jpg,.png"
              className={`pro-input ${getFieldError("image") && " error"}`}
              id="image"
              onBlur={formik.handleBlur("image")}
              onChange={(e) => handleImageChange(e, "image")}
            />
            <span className="input-drag-box">
              <IconText
                title={
                  isImageUploading
                    ? "Uploading..."
                    : imageName !== ""
                    ? imageName
                    : `Drop files to attach or browse`
                }
              />
            </span>
            {getFieldError("image") && (
              <span className="error-text">{getFieldError("image")}</span>
            )}
          </div>
          {imagePreview && (
            <div className={`col-2 pro-my-2`}>
              <Image width={100} height={100} src={imagePreview} alt={`plan image`} />
            </div>
          )}
        </div>

        <div className="input-wrap pro-mb-4">
          <label
            htmlFor="alias_image"
            className={`pro-font-sm pro-mb-1 pro-fw-medium`}
          >
            Plan Image (Arabic)
          </label>
          <div className="input-drag">
            <input
              type="file"
              accept=".jpeg,.jpg,.png"
              className={`pro-input ${getFieldError("alias_image") && " error"}`}
              id="alias_image"
              onBlur={formik.handleBlur("alias_image")}
              onChange={(e) => handleImageChange(e, "alias_image")}
            />
            <span className="input-drag-box">
              <IconText
                title={
                  isAliasImageUploading
                    ? "Uploading..."
                    : aliasImageName !== ""
                    ? aliasImageName
                    : `Drop files to attach or browse`
                }
              />
            </span>
            {getFieldError("alias_image") && (
              <span className="error-text">{getFieldError("alias_image")}</span>
            )}
          </div>
          {aliasImagePreview && (
            <div className={`col-2 pro-my-2`}>
              <Image
                width={100}
                height={100}
                src={aliasImagePreview}
                alt={`plan image arabic`}
              />
            </div>
          )}
        </div>

        <Input
          label={"Priority"}
          type="number"
          id="priority"
          name="priority"
          className={`pro-input lg ${getFieldError("priority") && " error"}`}
          {...fieldProps("priority")}
          error={getFieldError("priority")}
          errorMessage={getFieldError("priority")}
        />

        <div className="input-wrap pro-mb-4">
          <label
            htmlFor="audience"
            className="pro-font-sm pro-mb-1 pro-fw-medium"
          >
            Audience (corporate plans are sold only through offers) <Required />
          </label>
          <Select
            id="audience"
            name="audience"
            placeholder={"Select"}
            className={`pro-input lg ${getFieldError("audience") && " error"}`}
            classNamePrefix="pro-input"
            options={planAudiences ?? []}
            getOptionLabel={(option) => option?.name}
            getOptionValue={(option) => option?.id}
            value={audienceObj ?? []}
            onBlur={formik.handleBlur("audience")}
            onChange={(value) => setFieldValueTouched("audience", value?.id ?? 1)}
            menuPlacement="auto"
          />
          {getFieldError("audience") && (
            <span className="error-text">{getFieldError("audience")}</span>
          )}
        </div>

        <div className="input-wrap pro-mb-4">
          <label htmlFor="status" className="pro-font-sm pro-mb-1 pro-fw-medium">
            Status <Required />
          </label>
          <Select
            id="status"
            name="status"
            placeholder={"Select"}
            className={`pro-input lg ${getFieldError("status") && " error"}`}
            classNamePrefix="pro-input"
            options={planStatuses ?? []}
            getOptionLabel={(option) => option?.name}
            getOptionValue={(option) => option?.id}
            value={statusObj ?? []}
            onBlur={formik.handleBlur("status")}
            onChange={(value) => setFieldValueTouched("status", value?.id ?? 1)}
            menuPlacement="auto"
          />
          {getFieldError("status") && (
            <span className="error-text">{getFieldError("status")}</span>
          )}
        </div>
      </div>

      <div
        className={`col-12 pro-d-flex pro-justify-end ${
          isStickyFooter && "offcanvas-footer-sticky-btns"
        }`}
      >
        <Button
          className={"pro-btn-link lg pro-px-5"}
          onClick={() => closeModal()}
        >
          Cancel
        </Button>

        <Button
          className={`pro-btn-primary lg pro-ms-3`}
          type="submit"
          onClick={formik.handleSubmit}
          disabled={isPlanSaving || isImageUploading || isAliasImageUploading}
        >
          {!isEdit
            ? isPlanSaving
              ? "Creating..."
              : "Create"
            : isPlanSaving
            ? "Updating..."
            : "Update"}
        </Button>
      </div>
    </div>
  );
};

export default AddSubscriptionPlan;
